import {ArchiveRecord} from 'archive/ArchiveConstants';
import {ArchiveOptionsForModal} from 'archive/components/modals/ArchiveModal';
import {assert, assertAndContinue} from 'Asserts';
import {DataModificationsHeader} from 'audit/DataModifications';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {Container} from 'constants/SampleConstants';
import DocumentStore from 'notebook/documents/DocumentStore';
import {ParameterStructuredTable} from 'notebook/integrations/procedures/ParameterStructuredTableAdapter';
import {RegistrationDAOResponseBase} from 'notebook/integrations/registry/RegistrationActionUtil';
import {RegistrationStructuredTable} from 'notebook/integrations/registry/RegistrationStructuredTableAdapter';
import {ResultsTable} from 'notebook/integrations/results/ResultsTableConstants';
import {BoxCreationStructuredTable} from 'notebook/integrations/samples/BoxCreationStructuredTableAdapter';
import {InventoryStructuredTable} from 'notebook/integrations/samples/InventoryStructuredTableAdapter';
import {PlateCreationStructuredTable} from 'notebook/integrations/samples/PlateCreationStructuredTableAdapter';
import {TransferStructuredTable} from 'notebook/integrations/samples/TransferStructuredTableAdapter';
import {clientId} from 'notebook/store/VersioningConstants';
import {
  ActionFunctionContext,
  isActionFunctionContextEnabled,
} from 'notebook/structured-tables/action-functions/ActionFunctionContext';
import {ProcessOutputSubmitParams} from 'notebook/structured-tables/action-functions/submitProcessOutputsActionFunction';
import {EvaluatedCell} from 'notebook/tables/excess/EvaluationConstants';
import {CreateOrUpdateServerProcessTask} from 'processes/dao/ProcessManagementDAO';
import {ProcessOutputStructuredTable} from 'processes/tables/ProcessOutputStructuredTableAdapter';
import {ProcessTaskStructuredTable} from 'processes/tables/ProcessTaskStructuredTableAdapter';
// @ts-expect-error
import EntryStore from 'stores/EntryStore';
import APIUtil from 'util/APIUtil';
import * as net from 'util/Net';
import {HTTPHeaders} from 'util/Net';
import StageEntryStore from 'workflows/stage-entries/StageEntryStore';

import {RowSubmissionRange} from './action-functions/makeSubmitRowActionFunction';
import {
  OptionalTableValidationResponse,
  TABLE_VALIDATION_ERROR_MESSAGE,
  TableValidationError,
} from './StructuredTableApiConstants';
import {StructuredTable} from './StructuredTableConstants';

const enum ACTION_FUNCTIONS {
  PLATE_CREATION_SUBMIT = 'plate_creation',
  BOX_CREATION_SUBMIT = 'box_creation',
  REGISTRATION_SUBMIT = 'registration-submit',
  RESULT_SUBMIT = 'result-submit',
  TRANSFER_SUBMIT = 'transfer-submit',
  PROCESS_OUTPUT_SUBMIT = 'process-output-submit',
  PROCESS_TASK_SUBMIT = 'process-task-submit',
  PROCESS_TASK_UNDO_EXECUTION = 'process-task-undo-execution',
  ARCHIVE_ROWS = 'archive-rows',
  UNARCHIVE_ROWS = 'unarchive-rows',
  INVENTORY_SUBMIT = 'inventory-submit',
  ADD_DATASET_TO_EXISTING_ANALYSIS = 'add-dataset-to-existing-analysis',
  ADD_DATASET_TO_NEW_ANALYSIS = 'add-dataset-to-new-analysis',
  PARAMETER_SUBMIT = 'parameter-submit',
  RUN_VALIDATION = 'run-validation',
  RESYNC = 'resync',
}

interface RunActionArgs {
  entryId: string;
  provenanceUuid: string;
  actionName: string;
  evaluatedRows: Array<Array<EvaluatedCell>>;
  actionFunctionContext: ActionFunctionContext;
  selection?: RowSubmissionRange;
  latestVersionId?: number;
  extraParams?: object;
  extraHeaders?: HTTPHeaders;
}

export function getLatestVersionId(fileId: string): number | null {
  if (APIUtil.isEntry(fileId)) {
    return EntryStore.getEditorState(fileId)?.metadata.latestVersionId;
  } else if (APIUtil.isDocument(fileId)) {
    return DocumentStore.getEditorState(fileId)?.metadata.latestVersionId;
  } else if (APIUtil.isStageEntry(fileId)) {
    return StageEntryStore.getEditorState(fileId)?.metadata.latestVersionId;
  }
  assertAndContinue(false, 'Unexpected file type attempting to call structured table action function', {
    fileId,
  });
  return null;
}

export function _runAction<ActionResponse>(runActionArgs: RunActionArgs): Promise<ActionResponse> {
  const {
    entryId,
    provenanceUuid,
    actionName,
    evaluatedRows,
    actionFunctionContext,
    selection,
    extraParams,
    extraHeaders,
  } = runActionArgs;
  const latestVersionId = runActionArgs.latestVersionId ?? getLatestVersionId(entryId);
  if (selection) {
    assert(
      evaluatedRows.length === selection.end - selection.start,
      'Submission of partial table contains a different row count than indicated by selection'
    );
  }
  return net
    .postJSONPromise({
      url: `/1/api/entries/${entryId}/structured-tables/${provenanceUuid}/action/${actionName}`,
      data: {
        evaluatedRows,
        latestVersionId,
        clientId,
        startIndex: selection?.start,
        endIndex: selection?.end,
        ...(isActionFunctionContextEnabled() && {actionFunctionContext}),
        ...extraParams,
      },
      extraHeaders,
    })
    .catch((error) => {
      if (error?.responseJSON?.error === TABLE_VALIDATION_ERROR_MESSAGE) {
        throw new TableValidationError(error.responseJSON);
      }
      throw error;
    })
    .then((response) => {
      if (response?.error === TABLE_VALIDATION_ERROR_MESSAGE) {
        throw new TableValidationError(response);
      }
      return response;
    });
}

export interface RegistrationSubmitResponse extends RegistrationDAOResponseBase {}

export function register(
  entryId: string,
  structuredTable: RegistrationStructuredTable,
  evaluatedRows: Array<Array<EvaluatedCell>>,
  actionFunctionContext: ActionFunctionContext,
  selection?: RowSubmissionRange,
  latestVersionId?: number,
  extraParams: {allowMerging: boolean} = {allowMerging: false},
  dataModsHeader?: DataModificationsHeader
): Promise<RegistrationSubmitResponse> {
  return _runAction<RegistrationSubmitResponse>({
    entryId,
    provenanceUuid: structuredTable.provenanceUuid,
    actionName: ACTION_FUNCTIONS.REGISTRATION_SUBMIT,
    evaluatedRows,
    actionFunctionContext,
    selection,
    latestVersionId,
    extraParams,
    extraHeaders: dataModsHeader,
  });
}

export interface TransferSubmitResponse {
  destinationContainers: Array<Container>;
  entryVersionId: number;
}

export function transfer(
  entryId: string,
  structuredTable: TransferStructuredTable,
  evaluatedRows: Array<Array<EvaluatedCell>>,
  actionFunctionContext: ActionFunctionContext,
  selection?: RowSubmissionRange,
  latestVersionId?: number,
  dataModsHeader?: DataModificationsHeader
): Promise<TransferSubmitResponse> {
  return _runAction<TransferSubmitResponse>({
    entryId,
    provenanceUuid: structuredTable.provenanceUuid,
    actionName: ACTION_FUNCTIONS.TRANSFER_SUBMIT,
    evaluatedRows,
    actionFunctionContext,
    selection,
    latestVersionId,
    extraHeaders: dataModsHeader,
  });
}

export interface InventorySubmitResponse {
  containers: Array<Container>;
  entryVersionId: number;
}

export function inventorySubmit(
  entryId: string,
  structuredTable: InventoryStructuredTable,
  evaluatedRows: Array<Array<EvaluatedCell>>,
  actionFunctionContext: ActionFunctionContext,
  selection?: RowSubmissionRange,
  latestVersionId?: number,
  dataModsHeader?: DataModificationsHeader
): Promise<InventorySubmitResponse> {
  return _runAction<InventorySubmitResponse>({
    entryId,
    provenanceUuid: structuredTable.provenanceUuid,
    actionName: ACTION_FUNCTIONS.INVENTORY_SUBMIT,
    evaluatedRows,
    actionFunctionContext,
    selection,
    latestVersionId,
    extraHeaders: dataModsHeader,
  });
}

export interface CreatePlatesResponse {
  entryVersionId: number;
}

export interface CreateBoxesResponse {
  entryVersionId: number;
}

export function createPlates(
  entryId: string,
  structuredTable: PlateCreationStructuredTable,
  evaluatedRows: Array<Array<EvaluatedCell>>,
  actionFunctionContext: ActionFunctionContext,
  selection?: RowSubmissionRange,
  latestVersionId?: number,
  dataModsHeader?: DataModificationsHeader
): Promise<CreatePlatesResponse> {
  return _runAction<CreatePlatesResponse>({
    entryId,
    provenanceUuid: structuredTable.provenanceUuid,
    actionName: ACTION_FUNCTIONS.PLATE_CREATION_SUBMIT,
    evaluatedRows,
    actionFunctionContext,
    selection,
    latestVersionId,
    extraHeaders: dataModsHeader,
  });
}

export function createBoxes(
  entryId: string,
  structuredTable: BoxCreationStructuredTable,
  evaluatedRows: Array<Array<EvaluatedCell>>,
  actionFunctionContext: ActionFunctionContext,
  selection?: RowSubmissionRange,
  latestVersionId?: number,
  dataModsHeader?: DataModificationsHeader
): Promise<CreateBoxesResponse> {
  return _runAction<CreateBoxesResponse>({
    entryId,
    provenanceUuid: structuredTable.provenanceUuid,
    actionName: ACTION_FUNCTIONS.BOX_CREATION_SUBMIT,
    evaluatedRows,
    actionFunctionContext,
    selection,
    latestVersionId,
    extraHeaders: dataModsHeader,
  });
}

export interface ResultSubmitResponse {
  sampleIds: Array<string>;
  entryVersionId: number;
}

export function submitResults(
  entryId: string,
  structuredTable: ResultsTable,
  evaluatedRows: Array<Array<EvaluatedCell>>,
  actionFunctionContext: ActionFunctionContext,
  selection?: RowSubmissionRange,
  latestVersionId?: number,
  dataModsHeader?: DataModificationsHeader
): Promise<ResultSubmitResponse> {
  return _runAction<ResultSubmitResponse>({
    entryId,
    provenanceUuid: structuredTable.provenanceUuid,
    actionName: ACTION_FUNCTIONS.RESULT_SUBMIT,
    evaluatedRows,
    actionFunctionContext,
    selection,
    latestVersionId,
    extraHeaders: dataModsHeader,
  });
}

export interface ParameterSubmitResponse {
  entityIds: Array<string>;
}

export function submitParameters(
  entryId: string,
  structuredTable: ParameterStructuredTable,
  evaluatedRows: Array<Array<EvaluatedCell>>,
  actionFunctionContext: ActionFunctionContext,
  selection?: RowSubmissionRange,
  dataModsHeader?: DataModificationsHeader
): Promise<ParameterSubmitResponse> {
  return _runAction<ParameterSubmitResponse>({
    entryId,
    provenanceUuid: structuredTable.provenanceUuid,
    actionName: ACTION_FUNCTIONS.PARAMETER_SUBMIT,
    evaluatedRows,
    actionFunctionContext,
    selection,
    extraHeaders: dataModsHeader,
  });
}

export interface ProcessOutputSubmitResponse {
  outputIds: Array<string>;
  entryVersionId: number;
}

export function submitProcessOutputs(
  entryId: string,
  structuredTable: ProcessOutputStructuredTable,
  evaluatedRows: Array<Array<EvaluatedCell>>,
  actionFunctionContext: ActionFunctionContext,
  outputSubmitParams: ProcessOutputSubmitParams,
  batchInfo: ProcessesBatchInfo,
  selection?: RowSubmissionRange,
  latestVersionId?: number,
  dataModsHeader?: DataModificationsHeader
): Promise<ProcessOutputSubmitResponse> {
  return _runAction<ProcessOutputSubmitResponse>({
    entryId,
    provenanceUuid: structuredTable.provenanceUuid,
    actionName: ACTION_FUNCTIONS.PROCESS_OUTPUT_SUBMIT,
    evaluatedRows,
    actionFunctionContext,
    selection,
    latestVersionId,
    extraParams: {outputSubmitParams, batchInfo},
    extraHeaders: dataModsHeader,
  });
}

export interface ProcessTaskSubmitResponse {
  taskIds: Array<string>;
  entryVersionId: number;
}

export interface ProcessesBatchInfo {
  // Common ID across batches of a single submission
  actionId: string;
  // Flag indicating the last batch of the submission
  lastBatch: boolean;
}

export function submitProcessTasks(
  entryId: string,
  structuredTable: ProcessTaskStructuredTable,
  evaluatedRows: Array<Array<EvaluatedCell>>,
  actionFunctionContext: ActionFunctionContext,
  tasks: Array<CreateOrUpdateServerProcessTask>,
  batchInfo: ProcessesBatchInfo,
  selection?: RowSubmissionRange,
  latestVersionId?: number,
  dataModsHeader?: DataModificationsHeader
): Promise<ProcessTaskSubmitResponse> {
  return _runAction<ProcessTaskSubmitResponse>({
    entryId,
    provenanceUuid: structuredTable.provenanceUuid,
    actionName: ACTION_FUNCTIONS.PROCESS_TASK_SUBMIT,
    evaluatedRows,
    actionFunctionContext,
    selection,
    latestVersionId,
    extraParams: {tasks, batchInfo},
    extraHeaders: dataModsHeader,
  });
}

export function undoProcessTaskExecution(
  entryId: string,
  structuredTable: ProcessTaskStructuredTable,
  evaluatedRows: Array<Array<EvaluatedCell>>,
  actionFunctionContext: ActionFunctionContext,
  taskIds: Array<string>,
  dataModsHeader?: DataModificationsHeader
): Promise<{taskIds: Array<string>}> {
  return _runAction<{taskIds: Array<string>}>({
    entryId,
    provenanceUuid: structuredTable.provenanceUuid,
    actionName: ACTION_FUNCTIONS.PROCESS_TASK_UNDO_EXECUTION,
    evaluatedRows,
    actionFunctionContext,
    // TODO(BNCH-36124): Add appropriate table diff object
    extraParams: {taskIds},
    extraHeaders: dataModsHeader,
  });
}

interface ArchiveResponse {
  archiveRecordByApiId: {
    [itemId: string]: ArchiveRecord | null;
  };
}

export function archiveRows(
  entryId: string,
  structuredTable: StructuredTable<unknown, unknown>,
  evaluatedRows: Array<Array<EvaluatedCell>>,
  actionFunctionContext: ActionFunctionContext,
  items: Array<{modelType: ModelTypeConstants; id: string; rowId: string}>,
  archivePurpose: string,
  options?: ArchiveOptionsForModal,
  dataModsHeaders?: DataModificationsHeader
): Promise<ArchiveResponse> {
  // Backend archive_rows action function currently 400s when given a list of mixed type items
  assert(
    items.length === 0 || items.every((item) => item.modelType === items[0].modelType),
    'expected all items being archived to be of the same type'
  );
  return _runAction<ArchiveResponse>({
    entryId,
    provenanceUuid: structuredTable.provenanceUuid,
    actionName: ACTION_FUNCTIONS.ARCHIVE_ROWS,
    evaluatedRows,
    actionFunctionContext,
    extraParams: {
      items,
      archivePurpose,
      ...options,
    },
    extraHeaders: dataModsHeaders,
  });
}

export function unarchiveRows(
  entryId: string,
  structuredTable: StructuredTable<unknown, unknown>,
  evaluatedRows: Array<Array<EvaluatedCell>>,
  actionFunctionContext: ActionFunctionContext,
  items: Array<{modelType: ModelTypeConstants; id: string}>,
  dataModsHeaders?: DataModificationsHeader
): Promise<null> {
  return _runAction<null>({
    entryId,
    provenanceUuid: structuredTable.provenanceUuid,
    actionName: ACTION_FUNCTIONS.UNARCHIVE_ROWS,
    evaluatedRows,
    actionFunctionContext,
    extraParams: {items},
    extraHeaders: dataModsHeaders,
  });
}

export interface AddDatasetToNewAnalysisResponse {
  analysisId: string;
}

export interface AddDatasetToNewAnalysisParams {
  entryId: string;
  structuredTable: StructuredTable<unknown, unknown>;
  evaluatedRows: Array<Array<EvaluatedCell>>;
  actionFunctionContext: ActionFunctionContext;
  plainTextTable: Array<Array<string>>;
  dataFrameName: string;
  analysisName: string;
  projectId?: string;
  folderId?: string;
}

export function addDatasetToNewAnalysis({
  entryId,
  structuredTable,
  evaluatedRows,
  actionFunctionContext,
  plainTextTable,
  dataFrameName,
  analysisName,
  projectId,
  folderId,
}: AddDatasetToNewAnalysisParams): Promise<AddDatasetToNewAnalysisResponse> {
  return _runAction<AddDatasetToNewAnalysisResponse>({
    entryId,
    provenanceUuid: structuredTable.provenanceUuid,
    actionName: ACTION_FUNCTIONS.ADD_DATASET_TO_NEW_ANALYSIS,
    evaluatedRows,
    actionFunctionContext,
    extraParams: {
      plainTextTable,
      dataFrameName,
      analysisName,
      projectId,
      folderId,
    },
  });
}

export interface AddDatasetToExistingAnalysisResponse {
  dataFrameId: string;
}

export function addDatasetToExistingAnalysis(
  entryId: string,
  structuredTable: StructuredTable<unknown, unknown>,
  evaluatedRows: Array<Array<EvaluatedCell>>,
  actionFunctionContext: ActionFunctionContext,
  plainTextTable: Array<Array<string>>,
  dataFrameName: string,
  analysisId: string
): Promise<AddDatasetToExistingAnalysisResponse> {
  return _runAction<AddDatasetToExistingAnalysisResponse>({
    entryId,
    provenanceUuid: structuredTable.provenanceUuid,
    actionName: ACTION_FUNCTIONS.ADD_DATASET_TO_EXISTING_ANALYSIS,
    evaluatedRows,
    actionFunctionContext,
    extraParams: {
      plainTextTable,
      dataFrameName,
      analysisId,
    },
  });
}

export interface ResyncResponse {
  entryVersionId: number;
}

// Invokes the server-side re-sync action function (gated behind ELN_RESYNC_USE_BACKEND_ACTION_FUNCTION).
// Server-side equivalent of the client-side updateStructuredTableUsingRowUpdater refresh-all flow.
export function resync(
  entryId: string,
  structuredTable: StructuredTable<unknown, unknown>,
  evaluatedRows: Array<Array<EvaluatedCell>>,
  actionFunctionContext: ActionFunctionContext,
  selection?: RowSubmissionRange,
  latestVersionId?: number,
  dataModsHeader?: DataModificationsHeader
): Promise<ResyncResponse> {
  return _runAction<ResyncResponse>({
    entryId,
    provenanceUuid: structuredTable.provenanceUuid,
    actionName: ACTION_FUNCTIONS.RESYNC,
    evaluatedRows,
    actionFunctionContext,
    selection,
    latestVersionId,
    extraHeaders: dataModsHeader,
  });
}

export function validateStructuredTable(
  entryId: string,
  structuredTable: StructuredTable<unknown, unknown>,
  evaluatedRows: Array<Array<EvaluatedCell>>,
  actionFunctionContext: ActionFunctionContext,
  selection?: RowSubmissionRange,
  latestVersionId?: number,
  dataModsHeader?: DataModificationsHeader,
  onlyValidateUnfinalizedRows: boolean = true
): Promise<OptionalTableValidationResponse> {
  return _runAction<OptionalTableValidationResponse>({
    entryId,
    provenanceUuid: structuredTable.provenanceUuid,
    actionName: ACTION_FUNCTIONS.RUN_VALIDATION,
    evaluatedRows,
    actionFunctionContext,
    selection,
    latestVersionId,
    extraParams: {
      onlyValidateUnfinalizedRows,
    },
    extraHeaders: dataModsHeader,
  });
}
